import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ModalService } from 'src/utils/modal.service';
import { ToastService } from 'src/utils/toast.service';
import { ButtonsComponent } from '../buttons/buttons.component';
import { ERROR_MESSAGES } from 'src/utils/error-messages';
import { TooltipComponent } from '../tooltip/tooltip.component';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, FormsModule, ReactiveFormsModule, ButtonsComponent, TooltipComponent],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
})
export class ModalComponent {
  @Input() imageUrl: string = '';
  @Input() title: string = '';
  @Input() message: string = '';
  @Input() cancelButtonText: string = 'Cancelar';
  @Input() continueButtonText: string = 'Continuar';
  @Input() showCancelButton: boolean = false;
  @Input() bodyForm: FormGroup | null = null;
  @Output() close = new EventEmitter<void>();

  get modalVariant(): 'default' | 'legacy' {
  return this.modalService.modalVariant();
  }

  modalService = inject(ModalService);
  toastService = inject(ToastService);

  modalVisible = this.modalService.modalVisible;

  onClose(triggerCancel = false) {
  this.modalService.closeModal(triggerCancel);
  this.close.emit();
 }

  onConfirmClick() {
    if (this.modalService.onConfirm) {
      this.modalService.onConfirm();
    }

    if (this.modalService.showToastOnConfirm()) {
      this.toastService.showToast(
        'fi fi-ss-check-circle text-[24px] text-success',
        'Estado actualizado de manera exitosa',
        'success'
      );
    }
    this.modalService.closeModal();
  }

  getModalClasses(): string {
    const width = this.modalService.modalWidth() || '440px';
    if (this.modalVariant === 'legacy') {
      return `bg-white rounded-3xl shadow-custom pb-6 min-[481px]:pb-8 pt-8 min-[481px]:pt-12 px-4 min-[481px]:px-8 w-full min-[481px]:w-[${width}] max-w-[${width}]`;
    } else {
    const deleteFeatureClass = this.modalService.planName() ? 'modal-delete-feature' : '';
    return `bg-light rounded-2xl shadow-modal p-6 w-full border border-[#CBD4E1] max-w-[${width}] ${deleteFeatureClass}`;
    }
  }

  getModalWidth(): string {
    if (this.modalVariant === 'legacy') {
      return '';
    }
    return this.modalService.modalWidth() || '440px';
  }

  getModalWidthStyle(): {[key: string]: string} {
    if (this.modalVariant === 'legacy' || (this.modalService.customModalClasses() && this.modalService.customModalClasses().trim() !== '')) {
      return {};
    }
    const width = this.modalService.modalWidth() || '440px';
    return {
      '--modal-desktop-width': width
    };
  }

  getButtonClasses(buttonType: 'primary' | 'secondary' | 'danger'): string {
    if (this.modalVariant === 'legacy') {
      const baseClasses =
        'rounded-2xl font-poppins h-12 transition-all';
      const sharedWidthClasses = 'w-full min-[481px]:flex-1';

      switch (buttonType) {
        case 'primary':
          return `${baseClasses} ${sharedWidthClasses} px-0 font-medium text-[16px] leading-[24px] text-white bg-primary-g300 focus:outline-none`;
        case 'secondary':
          return `${baseClasses} ${sharedWidthClasses} px- font-medium text-[16px] leading-[24px] text-[#1A212B] bg-transparent focus:outline-none`;
        case 'danger':
          return `${baseClasses} px-0 whitespace-nowrap font-medium text-[16px] leading-[24px] text-white bg-error focus:outline-none`;
        default:
          return `${baseClasses} ${sharedWidthClasses} px-0 font-medium text-[16px] leading-[24px] text-white bg-primary-g300 focus:outline-none`;
      }
    } else {

    const baseClasses =
      'rounded-2xl font-poppins font-medium px-4 h-12 transition-all text-base';

    switch (buttonType) {
      case 'primary':
        return `${baseClasses} text-[16px] bg-primary-g300 text-white focus:outline-none`;
      case 'secondary':
        return `${baseClasses} text-[16px] leading-[24px] bg-transparent text-secundary-b300 focus:outline-none`;
      case 'danger':
        return `${baseClasses} text-[16px] leading-[24px] bg-error text-white focus:outline-none`;
      default:
        return `${baseClasses} bg-primary-g300 text-white focus:outline-none`;
    }
  }
  }
  getTitleClasses(): string {
    if (this.modalVariant === 'legacy') {
      return 'font-poppins text-center';
    } else {
    return 'font-poppins font-semibold text-[22px] leading-[24px] text-[#1A212B] text-center';
  }
  }
  getMessageClasses(): string {
  return 'font-poppins font-medium text-[14px] text-text-primary text-center leading-5 mt-[4px]';
  }

  getIconClass(): string {
    const iconClass = this.modalService.iconClass();
    return iconClass.replace(/text-\[[^\]]+\]/g, '').trim();
  }

  getIconSize(): number {
     if (this.modalVariant === 'legacy') {
      const iconClass = this.modalService.iconClass();
      const sizeMatch = iconClass.match(/text-\[(\d+)px\]/);
      return sizeMatch ? parseInt(sizeMatch[1]) : 34;
    } else {
   return 40;
  }
  }

  getIconBackgroundColor(): string {
    switch (this.modalService.modalType()) {
      case 'success':
        return '#E1FCDE';
        return '#E1FCDE';
      case 'warning':
        return '#FFD4D8';
        return '#FFD4D8';
      case 'confirmation':
        return '#FFF2D2';
        return '#FFF2D2';
      case 'info':
        return '#FFF2D2';
      default:
        return '#4fd1c5';
    }
  }

  getIconShadowColor(): string {
    const backgroundColor = this.getIconBackgroundColor();
    return this.lightenColor(backgroundColor, 0);
    return this.lightenColor(backgroundColor, 0);
  }

  getCircleSizes() {
     if (this.modalVariant === 'legacy') {
      const iconSize = this.getIconSize();
      return {
        outer: iconSize + 40,
      };
    } else {
    return {
      outer: 72,
    };
  }
  }

  getIconColor(): string {
    switch (this.modalService.modalType()) {
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-error';
      case 'confirmation':
        return 'text-warning';
      default:
        return 'text-primary-g300';
    }
  }

  private lightenColor(color: string, amount: number): string {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const newR = Math.round(r + (255 - r) * amount);
    const newG = Math.round(g + (255 - g) * amount);
    const newB = Math.round(b + (255 - b) * amount);

    return `rgb(${newR}, ${newG}, ${newB})`;
  }

  onConfirmMiddle() {
    if (this.modalService.onConfirmMiddle) {
      this.modalService.onConfirmMiddle();
    }
    this.modalService.closeModal();
  }

  get controls() {
    return Object.keys(this.modalService.bodyForm()?.controls || {});
  }

  getErrorMessage(controlName: string): string {
    const control = this.modalService.bodyForm()?.get(controlName);
    if (!control || !control.errors) {
      return '';
    }

    const errorKey = Object.keys(control.errors)[0];
    const errorValue = control.errors[errorKey];
    
    // Si el error tiene un campo 'message', usarlo directamente
    if (errorValue && typeof errorValue === 'object' && 'message' in errorValue) {
      return errorValue.message;
    }
    
    // Si el errorValue es un string, usarlo directamente
    if (typeof errorValue === 'string') {
      return errorValue;
    }

    const errorMessageHandler = ERROR_MESSAGES[errorKey];

    if (typeof errorMessageHandler === 'function') {
      return errorMessageHandler(errorValue);
    }

    return errorMessageHandler || 'Error de validación.';
  }

}
